import type { FormEvent } from "react";
import { LoaderCircle, LockKeyhole } from "lucide-react";
import { useState } from "react";
import { Navigate } from "react-router-dom";

import { PasswordField } from "@/components/PasswordField";
import { useSession } from "@/context/SessionContext";
import { changePassword } from "@/lib/api";

export function ProfilePage() {
  const { session, setSession } = useSession();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!session) {
    return <Navigate replace to="/auth?role=candidate&mode=signin" />;
  }

  const activeSession = session;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (newPassword !== confirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await changePassword({
        currentPassword,
        newPassword
      });

      if (activeSession.role === "admin" && response.user.role === "admin") {
        setSession({
          role: "admin",
          token: response.token,
          expiresAt: response.expiresAt,
          user: response.user
        });
      } else if (activeSession.role === "candidate" && response.user.role === "candidate") {
        setSession({
          role: "candidate",
          token: response.token,
          expiresAt: response.expiresAt,
          user: response.user
        });
      }

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setMessage(response.message);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to change password.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const heading =
    activeSession.role === "admin" ? activeSession.user.name : activeSession.user.fullName;

  return (
    <section className="mx-auto max-w-[960px] px-6 py-10 md:py-12" data-testid="profile-page">
      <div className="surface p-8 md:p-10" data-testid="profile-card">
        <p className="eyebrow">Profile</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight text-ink" data-testid="profile-heading">
          Password and account settings
        </h1>
        <p className="mt-4 text-sm leading-7 text-slate" data-testid="profile-session-user">
          Signed in as <strong>{heading}</strong> ({activeSession.user.email})
        </p>

        <form className="mt-8 grid gap-5" data-testid="profile-form" onSubmit={handleSubmit}>
          <label className="block space-y-2.5">
            <span className="field-label">Current password</span>
            <PasswordField
              data-testid="profile-current-password"
              minLength={8}
              onChange={(event) => setCurrentPassword(event.target.value)}
              required
              toggleButtonTestId="profile-current-password-toggle"
              value={currentPassword}
            />
          </label>

          <label className="block space-y-2.5">
            <span className="field-label">New password</span>
            <PasswordField
              data-testid="profile-new-password"
              minLength={8}
              onChange={(event) => setNewPassword(event.target.value)}
              required
              toggleButtonTestId="profile-new-password-toggle"
              value={newPassword}
            />
          </label>

          <label className="block space-y-2.5">
            <span className="field-label">Confirm new password</span>
            <PasswordField
              data-testid="profile-confirm-password"
              minLength={8}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              toggleButtonTestId="profile-confirm-password-toggle"
              value={confirmPassword}
            />
            {confirmPassword.length > 0 && newPassword.length > 0 ? (
              <p className={`text-xs font-medium ${confirmPassword === newPassword ? "text-emerald-600" : "text-rose-600"}`}>
                {confirmPassword === newPassword ? "Passwords match" : "Passwords do not match"}
              </p>
            ) : null}
          </label>

          {error ? (
            <div
              className="rounded-[22px] border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-medium text-rose-700"
              data-testid="profile-error-message"
            >
              {error}
            </div>
          ) : null}

          {message ? (
            <div
              className="rounded-[22px] border border-emerald-200 bg-emerald-50 px-5 py-4 text-sm font-medium text-emerald-800"
              data-testid="profile-success-message"
            >
              {message}
            </div>
          ) : null}

          <div className="flex justify-end">
            <button className="btn-primary" data-testid="profile-submit" disabled={isSubmitting} type="submit">
              {isSubmitting ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <LockKeyhole className="h-4 w-4" />
              )}
              Update Password
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
